import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useLocalStorage } from '@/shared/lib/useLocalStorage'

import { TOUR_CONFIGS } from '../config/tours'
import { useDriverContext } from '../DriverContext'
import { ReadinessCheck, TourDependency, TourName } from '../types/Tour'

const DEFAULT_MAX_ATTEMPTS = 10
const DEFAULT_INTERVAL_MS = 500

const checkReadiness = (check: ReadinessCheck): boolean => {
  const selectors = Array.isArray(check.elementSelector)
    ? check.elementSelector
    : [check.elementSelector]

  return selectors.every(selector => {
    const element = document.querySelector(selector)

    return !!element
  })
}

const checkDependencies = (dependencies?: TourDependency[]): boolean => {
  const areDependenciesMet = dependencies?.every(dep => {
    const isDependencyCompleted = localStorage.getItem(dep.storageKey) === 'true'

    return isDependencyCompleted
  }) ?? true

  return areDependenciesMet
}

export const useTour = (tourName: TourName, isPageLoaded: boolean) => {
  const tourConfig = useMemo(() => TOUR_CONFIGS[tourName], [tourName])
  const { driverInstance, startTour, setSteps, setConfig } = useDriverContext()
  const [isTourCompleted, setTourCompleted] = useLocalStorage<boolean>(tourConfig.storageKey, false)
  const [isReady, setIsReady] = useState(!tourConfig.readinessCheck)
  const attemptsRef = useRef(0)

  // Readiness check logic
  useEffect(() => {
    if (!isPageLoaded || !tourConfig.readinessCheck || isReady || isTourCompleted) {
      return
    }

    const {
      maxAttempts = DEFAULT_MAX_ATTEMPTS,
      intervalMs = DEFAULT_INTERVAL_MS,
    } = tourConfig.readinessCheck

    attemptsRef.current = 0

    const intervalId = setInterval(() => {
      attemptsRef.current += 1

      if (attemptsRef.current >= maxAttempts) {
        console.warn(`Tour ${tourName} readiness check failed after ${maxAttempts} attempts`)
        clearInterval(intervalId)

        return
      }

      if (checkReadiness(tourConfig.readinessCheck!) && checkDependencies(tourConfig.dependencies)) {
        setIsReady(true)
        clearInterval(intervalId)
        console.log(`Tour ${tourName} ready after ${attemptsRef.current} attempts`)
      }
    }, intervalMs)

    return () => clearInterval(intervalId)
  }, [isPageLoaded, tourConfig.readinessCheck, tourConfig.dependencies, isReady, isTourCompleted, tourName])

  // Start tour when everything is ready
  useEffect(() => {
    if (!isPageLoaded || !driverInstance || !isReady || isTourCompleted) {
      return
    }

    setSteps(tourConfig.steps)
    setConfig({
      onDestroyed: () => {
        setTourCompleted(true)
      },
    })

    const timer = setTimeout(() => {
      console.log(`Starting ${tourName} tour...`)
      startTour()
    }, 1500)

    return () => {
      clearTimeout(timer)
    }
  }, [
    isPageLoaded,
    driverInstance,
    isReady,
    isTourCompleted,
    tourName,
    setSteps,
    setConfig,
    startTour,
    tourConfig.steps,
    setTourCompleted,
  ])

  const resetTour = useCallback(() => {
    setTourCompleted(false)
    setIsReady(!tourConfig.readinessCheck)
    attemptsRef.current = 0
    localStorage.removeItem('active-tour')
  }, [setTourCompleted, tourConfig.readinessCheck])

  return {
    isTourCompleted,
    isReady,
    resetTour,
  }
}